import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from '../../../constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { ActionBase } from '../../../types/iapBaseTypes';
import { toUtcIso } from '../../../utils/dateTime';
import { IapScope, ORGANIZATION } from '../ICSConstants';

export type OrgActionBase = ActionBase & {
  id: UUID;
  action_id: UUID;
  poc: string | null;
  briefed_poc: boolean;
  start_date: Date | null;
  status: string | null;
  target_date: Date | null;
  actual_date: Date | null;
  utc_offset?: number;
  parent_id?: UUID | null;
};

export type GetOrgActionResponse = OrgActionBase & {
  group_guid: UUID;
  user_guid: UUID;
  user_name?: string;
  poc_user_name?: string;
  version: number;
  timestamp: Date;
  index?: number;
};

export type CreateOrgActionReqData = ActionBase & {
  user_guid?: UUID;
  poc?: string | null;
  briefed_poc?: boolean;
  start_date?: Date | null;
  status?: string | null;
  target_date?: Date | null;
  actual_date?: Date | null;
  parent_id?: UUID | null;
  utc_offset?: number;
};

export type EditOrgActionRequest = CreateOrgActionReqData & {
  action_id: UUID;
  archived?: boolean;
};

export const keys = {
  base: ['org_actions'] as const,
  byGroupAndUser: (groupGuid: string, userId: string) =>
    [...keys.base, groupGuid, userId] as const,
} as const;

export const useOrgActions = (scope: IapScope = ORGANIZATION) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.byGroupAndUser(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<GetOrgActionResponse[]>(
        `${config.apiGateway.ics_org_actions}/${group_guid}`
      );
      return resp.data.map((a) => ({
        ...a,
        start_date: toUtcIso(
          a.start_date
        ) as GetOrgActionResponse['start_date'],
        target_date: toUtcIso(
          a.target_date
        ) as GetOrgActionResponse['target_date'],
        actual_date: toUtcIso(
          a.actual_date
        ) as GetOrgActionResponse['actual_date'],
        timestamp: toUtcIso(a.timestamp) as GetOrgActionResponse['timestamp'],
      }));
    },
    enabled: scope === ORGANIZATION && !!group_guid,
  });
};

export const useCreateOrgAction = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateOrgActionReqData) => {
      return await axios.post<GetOrgActionResponse>(
        `${config.apiGateway.ics_org_actions}/${group_guid}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.byGroupAndUser(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useUpdateOrgAction = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditOrgActionRequest) => {
      return await axios.put(
        `${config.apiGateway.ics_org_actions}/${group_guid}/${data.action_id}`,
        { ...data, user_guid, archived: data.archived ?? false }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.byGroupAndUser(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
